import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { InputText, useModal, useToastr } from '@farmshare/ui-components';
import { ImageUploadInput } from '../components/builder/image-upload-input';
import { useCatalogState } from '../hooks';
import {
  CatalogItemCard,
  CutForm,
  ExtraForm,
  SortableCatalogGrid,
  SortablePrimalSidebar,
  SpecificationForm,
  TrimForm,
} from '../components';
import type {
  Cut,
  Extra,
  ItemPricing,
  Primal,
  Specification,
  Trim,
} from '../types';
import { removeTypename } from '../../../../../../utils/src/lib/utils';
import { min } from 'lodash';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children = null, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

interface CutsheetCatalogPageProps {
  /** Optional callback to navigate to questionnaire builder */
  onNavigateToQuestionnaire?: () => void;
  /** Vendor ID for fetching capabilities */
  vendorId?: string;
  /** Capability ID for fetching catalog items */
  capabilityId?: string;
}

/**
 * Main Cutsheet V2 Catalog Management Page
 *
 * This is the first page of the new cutsheet system where processors define
 * their available options (primals, cuts, trim, extras) and specifications
 * before building cutsheet wizards.
 * @param props - Component props
 * @param props.onNavigateToQuestionnaire - Optional callback to navigate to questionnaire builder
 * @param props.vendorId - Vendor ID for fetching capabilities
 * @param props.capabilityId - Capability ID for fetching catalog items
 * @returns Catalog page component
 */
export const CutsheetCatalogPage: FC<CutsheetCatalogPageProps> = ({
  onNavigateToQuestionnaire: _onNavigateToQuestionnaire = undefined,
  vendorId = undefined,
  capabilityId = undefined,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { save, ask } = useModal();
  const { push } = useToastr();

  // Guard: Require vendorId and capabilityId
  if (!vendorId || !capabilityId) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {!vendorId
              ? 'Vendor information required'
              : 'Please select a capability'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const {
    catalog: capabilities,
    addCut,
    updateCut,
    removeCut,
    reorderCuts,
    addPrimal,
    updatePrimal,
    removePrimal,
    addTrim,
    updateTrim,
    removeTrim,
    reorderTrim,
    addSpecification,
    updateSpecification,
    removeSpecification,
    reorderSpecifications,
    reorderPrimals,
  } = useCatalogState({
    vendorId,
    capabilityId,
    isActive: true,
  });

  // Build a map of groupId -> cut count for the sidebar
  const cutCountsMap = useMemo(
    () =>
      capabilities.primals.reduce<Record<string, number>>((acc, primal) => {
        acc[primal.id] = capabilities.cuts.filter(
          (c) => c.groupId === primal.id,
        ).length;
        return acc;
      }, {}),
    [capabilities.primals, capabilities.cuts],
  );

  // Handler for deleting a Primal (only allowed when it has 0 cuts)
  const handleDeletePrimal = (primal: Primal) => {
    ask({
      type: 'ask',
      title: 'Delete Group?',
      body: `Are you sure you want to delete the "${primal.title}" group? This cannot be undone.`,
      onConfirm: () => {
        removePrimal(primal.id);
        setActiveTab(0);
        push({
          title: 'Success',
          body: `"${primal.title}" group deleted successfully`,
          bg: 'success',
        });
      },
    });
  };

  // Handler for renaming a Primal
  const handleRenamePrimal = (primal: Primal) => {
    save({
      type: 'save',
      title: 'Rename Group',
      initialValues: {
        title: primal.title,
      },
      body: <InputText label="Name" nameOverride="title" required />,
      onSubmit: async (values) => {
        await updatePrimal(primal.id, { title: values.title as string });
        push({
          title: 'Success',
          body: `Group renamed to "${values.title}"`,
          bg: 'success',
        });
      },
    });
  };

  // Handler for adding a new Primal
  const handleAddPrimal = () => {
    save({
      type: 'save',
      title: 'Add Group',
      initialValues: {
        title: '',
        imageUrl: '',
      },
      body: <InputText label="Name" nameOverride="title" required />,
      onSubmit: async (values) => {
        addPrimal(
          values as Omit<
            Primal,
            'id' | 'createdAt' | 'updatedAt' | 'type' | 'capabilityId'
          >,
        );
        push({
          title: 'Success',
          body: 'Primal added successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for adding a new Cut
  const handleAddCut = (groupId?: string) => {
    save({
      type: 'save',
      title: 'Add Cut',
      hideHeader: true,
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        groupId: groupId || '',
        printedText: '',
        type: 'cut',
        pricing: {
          model: '' as ItemPricing['model'],
          price: 0,
          minimumCharge: undefined,
        },
      },
      validate: (values) => {
        const errors: Record<string, string> = {};
        if (!(values.title as string)?.trim()) {
          errors.title = 'Name is required';
        }
        if (!values.groupId) {
          errors.groupId = 'Group is required';
        }
        return errors;
      },
      body: (
        <CutForm primals={capabilities.primals} capabilityId={capabilityId} />
      ),
      onSubmit: async (values) => {
        // The hook guards against empty model ("No Additional Charge") before
        // sending to the API, so pass values through directly.
        addCut(
          values as Omit<
            Cut,
            'id' | 'createdAt' | 'updatedAt' | 'capabilityId'
          >,
        );
        push({
          title: 'Success',
          body: 'Cut added successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for editing a Cut
  const handleEditCut = (cut: Cut) => {
    // Any spec can be attached to any item; offer all specs for assignment.
    const cutTypeSpecs = capabilities.specifications;

    // IDs of specs currently assigned to this cut
    const initialSpecIds = cutTypeSpecs
      .filter((s) => s.assignedToIds?.includes(cut.id))
      .map((s) => s.id);

    save({
      type: 'save',
      title: 'Edit Cut',
      hideHeader: true,
      initialValues: {
        title: cut.title,
        description: cut.description || '',
        imageUrl: cut.imageUrl || '',
        groupId: cut.groupId || '',
        printedText: cut.printedText || '',
        pricing: cut.pricing ?? {
          model: '' as ItemPricing['model'],
          price: 0,
          minimumCharge: undefined,
        },
        specificationIds: initialSpecIds,
      },
      validate: (values) => {
        const errors: Record<string, string> = {};
        if (!(values.title as string)?.trim()) {
          errors.title = 'Name is required';
        }
        if (!values.groupId) {
          errors.groupId = 'Group is required';
        }
        return errors;
      },
      body: (
        <CutForm
          primals={capabilities.primals}
          capabilityId={capabilityId}
          specifications={cutTypeSpecs}
        />
      ),
      onSubmit: async (values) => {
        const { specificationIds, ...cutValues } = values as {
          specificationIds: string[];
          [key: string]: unknown;
        };

        updateCut(cut.id, cutValues as Partial<Cut>);

        // Sync specification assignments: diff old vs new
        const added = specificationIds.filter(
          (id) => !initialSpecIds.includes(id),
        );
        const removed = initialSpecIds.filter(
          (id) => !specificationIds.includes(id),
        );

        for (const specId of added) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec) {
            updateSpecification(specId, {
              assignedToIds: [...(spec.assignedToIds || []), cut.id],
            });
          }
        }

        for (const specId of removed) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec) {
            updateSpecification(specId, {
              assignedToIds: (spec.assignedToIds || []).filter(
                (id) => id !== cut.id,
              ),
            });
          }
        }

        push({
          title: 'Success',
          body: 'Cut updated successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for deleting a Cut
  const handleDeleteCut = (id: string) => {
    const cut = capabilities.cuts.find((c) => c.id === id);
    if (!cut) return;

    ask({
      type: 'ask',
      title: 'Delete Cut?',
      body: `Are you sure you want to delete "${cut.title}"? This cannot be undone.`,
      onConfirm: () => {
        removeCut(id);
        push({
          title: 'Success',
          body: 'Cut deleted successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for adding a new Trim
  const handleAddTrim = () => {
    save({
      type: 'save',
      title: 'Add Trim Option',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        isDefault: false,
        minQty: 0,
        printedText: '',
        type: 'trim',
        pricing: {
          model: '' as ItemPricing['model'],
          price: 0,
          minimumCharge: undefined,
        },
      },
      hideHeader: true,
      body: <TrimForm capabilityId={capabilityId} />,
      onSubmit: async (values) => {
        addTrim(
          removeTypename(values) as Omit<
            Trim,
            'id' | 'createdAt' | 'updatedAt' | 'capabilityId'
          >,
        );
        push({
          title: 'Success',
          body: 'Trim option added successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for editing Trim
  const handleEditTrim = (trim: Trim) => {
    save({
      type: 'save',
      title: 'Edit Trim Option',
      initialValues: {
        title: trim.title,
        description: trim.description || '',
        imageUrl: trim.imageUrl || '',
        printedText: trim.printedText || '',
        minQty: trim.minQty,
        pricing: trim.pricing ?? {
          model: '' as ItemPricing['model'],
          price: 0,
          minimumCharge: undefined,
        },
      },
      body: <TrimForm capabilityId={capabilityId} />,
      onSubmit: async (values) => {
        updateTrim(trim.id, removeTypename(values) as Partial<Trim>);
        push({
          title: 'Success',
          body: 'Trim option updated successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for deleting Trim
  const handleDeleteTrim = (id: string) => {
    const trim = capabilities.trim.find((t) => t.id === id);
    if (!trim) return;

    ask({
      type: 'ask',
      title: 'Delete Trim Option?',
      body: `Are you sure you want to delete "${trim.title}"? This cannot be undone.`,
      onConfirm: () => {
        removeTrim(id);
        push({
          title: 'Success',
          body: 'Trim option deleted successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for adding a new Specification
  const handleAddSpec = () => {
    save({
      type: 'save',
      title: 'Add Specification',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        inputType: 'text',
        options: [],
        unit: '',
        required: false,
        printedText: '',
        assignedToIds: [],
        type: 'specification',
      },
      body: (
        <SpecificationForm
          cuts={capabilities.cuts}
          trim={capabilities.trim}
          extras={capabilities.extras}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addSpecification(
          values as Omit<
            Specification,
            'id' | 'createdAt' | 'updatedAt' | 'capabilityId'
          >,
        );
        push({
          title: 'Success',
          body: 'Specification added successfully',
          bg: 'success',
        });
      },
    });
  };

  // Handler for editing a Specification
  const handleEditSpec = (spec: Specification) => {
    save({
      type: 'save',
      title: 'Edit Specification',
      hideHeader: true,
      initialValues: {
        title: spec.title,
        description: spec.description || '',
        imageUrl: spec.imageUrl || '',
        inputType: spec.inputType,
        options: spec.options || [],
        unit: spec.unit || '',
        required: spec.required || false,
        printedText: spec.printedText || '',
        assignedToIds: spec.assignedToIds || [],
      },
      body: (
        <SpecificationForm
          cuts={capabilities.cuts}
          trim={capabilities.trim}
          extras={capabilities.extras}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        updateSpecification(spec.id, values as Partial<Specification>);
        push({
          title: 'Success',
          body: 'Specification updated successfully',
          bg: 'success',
        });
      },
    });
  };

  return (
    <Stack direction="row" spacing={2} component={Card} sx={{ p: 2, my: 2 }}>
      <SortablePrimalSidebar
        primals={capabilities.primals}
        cutCounts={cutCountsMap}
        trimCount={capabilities.trim.length}
        extrasCount={capabilities.extras.length}
        specificationsCount={capabilities.specifications.length}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onReorder={reorderPrimals}
        onAddPrimal={handleAddPrimal}
      />

      <Box sx={{ flex: 1 }}>
        {/* Primal Tabs - Dynamic based on primals */}
        {capabilities.primals.map((primal, primalIndex) => (
          <TabPanel key={primal.id} value={activeTab} index={primalIndex}>
            <Stack spacing={3}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6">{primal.title}</Typography>
                  <Tooltip title="Rename group">
                    <Button
                      size="small"
                      sx={{ minWidth: 'auto', p: 0.5 }}
                      onClick={() => handleRenamePrimal(primal)}
                    >
                      <EditIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip
                    title={
                      cutCountsMap[primal.id] > 0
                        ? 'Remove all cuts from this group before deleting it'
                        : 'Delete this group'
                    }
                  >
                    {/* Span needed so Tooltip works on a disabled button */}
                    <span>
                      <Button
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeletePrimal(primal)}
                        variant="outlined"
                        color="error"
                        disabled={cutCountsMap[primal.id] > 0}
                      >
                        Delete Group
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddCut(primal.id)}
                    variant="contained"
                  >
                    Add Cut
                  </Button>
                </Stack>
              </Stack>

              {capabilities.cuts.filter((c) => c.groupId === primal.id)
                .length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No cuts added for {primal.title} yet. Add cuts to define
                    what this primal can be processed into.
                  </Typography>
                </Box>
              ) : (
                <SortableCatalogGrid
                  items={capabilities.cuts.filter(
                    (c) => c.groupId === primal.id,
                  )}
                  onReorder={reorderCuts}
                  renderItem={(item) => (
                    <CatalogItemCard
                      item={item}
                      onEdit={() => handleEditCut(item as Cut)}
                      onDelete={handleDeleteCut}
                      showPrimalBadge
                      primalName={primal.title}
                    />
                  )}
                  emptyMessage={`No cuts added for ${primal.title} yet.`}
                />
              )}
            </Stack>
          </TabPanel>
        ))}

        {/* Trim Tab */}
        <TabPanel value={activeTab} index={capabilities.primals.length}>
          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Trim Options</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTrim}
                variant="contained"
              >
                Add Trim
              </Button>
            </Stack>

            <SortableCatalogGrid
              items={capabilities.trim}
              onReorder={reorderTrim}
              renderItem={(item) => (
                <CatalogItemCard
                  item={item}
                  onEdit={() => handleEditTrim(item as Trim)}
                  onDelete={handleDeleteTrim}
                />
              )}
              emptyMessage="No trim options added yet. Trim options define how meat should be trimmed (e.g., fat cap thickness)."
            />
          </Stack>
        </TabPanel>

        {/* Specifications Tab */}
        <TabPanel value={activeTab} index={capabilities.primals.length + 2}>
          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Specifications</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddSpec}
                variant="contained"
              >
                Add Specification
              </Button>
            </Stack>

            <SortableCatalogGrid
              items={capabilities.specifications}
              onReorder={reorderSpecifications}
              renderItem={(item) => (
                <CatalogItemCard
                  item={item}
                  onEdit={() => handleEditSpec(item as Specification)}
                  onDelete={removeSpecification}
                  hideImage
                />
              )}
              emptyMessage="No specifications added yet."
            />
          </Stack>
        </TabPanel>
      </Box>
    </Stack>
  );
};
